import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { store } from 'redux/store';
import {
  selectDVCPMATrackingForecastGroupId,
  selectDVCPMATrackingForecastMultiGroupIds,
  setGroupId,
  setMultiGroupIds,
} from 'redux/reducers/meteorologyAgroTrackingForecastDetailedViewControlPanelReducer';
import useGroups from 'hooks/Meteorology/Agro/useLocationGroups';

import type Group from 'types/Locations/Group';

export default function useGroup() {
  const { groups: baseGroups } = useGroups();
  const selectedGroupId: number | undefined = useSelector(selectDVCPMATrackingForecastGroupId);
  const selectedMultiGroupIds: number[] = useSelector(selectDVCPMATrackingForecastMultiGroupIds);

  const groups = useMemo(() => baseGroups || [], [baseGroups]);

  const selectedGroup = useMemo(() => {
    if (!selectedGroupId || !groups.length) return undefined;
    return groups.find((group) => group.id === selectedGroupId);
  }, [selectedGroupId, groups]);

  const selectedMultiGroups = useMemo(() => {
    if (!selectedMultiGroupIds.length || !groups.length) return [];
    return selectedMultiGroupIds
      .map((id) => groups.find((group) => group.id === id))
      .filter((group): group is Group => group !== undefined);
  }, [selectedMultiGroupIds, groups]);

  const setGroup = useCallback((id: number) => {
    const group = groups.find((g) => g.id === id);
    if (group) store.dispatch(setGroupId(id));
  }, [groups]);

  const isGroupSelected = useCallback((id: number) => (
    selectedMultiGroupIds.includes(id)
  ), [selectedMultiGroupIds]);

  const toggleMultiGroup = useCallback((id: number) => {
    const group = groups.find((g) => g.id === id);

    if (!group) return false;

    if (isGroupSelected(group.id)) {
      const newGroupIds = selectedMultiGroupIds.filter((gId) => gId !== group.id);
      store.dispatch(setMultiGroupIds(newGroupIds));

      return false;
    }

    const newGroupIds = [...selectedMultiGroupIds, group.id];
    store.dispatch(setMultiGroupIds(newGroupIds));

    return true;
  }, [groups, isGroupSelected, selectedMultiGroupIds]);

  return {
    groups,
    selectedGroup,
    selectedMultiGroups,
    setGroup,
    isGroupSelected,
    toggleMultiGroup,
  };
}
